// src/App.js
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import MainPage from './pages/MainPage';
import NotFoundPage from './pages/NotFoundPage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/user/LoginPage';
import RegisterPage from './pages/user/RegisterPage';
import SettingsPage from './pages/user/SettingsPage';
import ProgressContentPage from './pages/user/ProgressContentPage';
import SubscriptionPage from './pages/user/SubscriptionPage';
import DashboardPage from './pages/user/DashboardPage';
import ActionPage from './pages/user/ActionPage';
import CourseContentPage from './pages/CourseContentPage';
import SearchPage from './pages/SearchPage';

import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';

import { ModalProvider, useModal } from './context/ModalContext';
import { ToastProvider } from './context/ToastContext';
import OfflineOverlay from './components/common/OfflineOverlay';
import { loadPayPalSdk } from './utils/loadPayPalSdk';
import CrispChat from './utils/crispChat';
import { useAuth } from './firebase/useAuth';
// --- 1. IMPORT BOTH ROUTE GUARDS ---
import PublicRoute from './components/auth/PublicRoute';
import PrivateRoute from './components/auth/PrivateRoute';

import LearnSessionPage from './pages/session/LearnSessionPage';
import ReviewSentencesSessionPage from './pages/session/ReviewSentencesSessionPage';
import ReviewWordsSessionPage from './pages/session/ReviewWordsSessionPage';
import CramSentencesSessionPage from './pages/session/CramSentencesSessionPage';
import CramWordsSessionPage from './pages/session/CramWordsSessionPage';
import SessionEndPage from './pages/session/SessionEndPage';

// SANDBOX
// const backendURL = 'http://127.0.0.1:5001/kikikata/us-central1';
// const PAYPAL_CLIENT_ID = 'AX89FzEmUOMPlt4qFlNJp4uYZZoHkSs67Y1satrSTBT4O3sJ9r7PEgMp0tgSe2mukZEiwuaDVJDhwZSb';
// const PAYPAL_MONTHLY_PLAN_ID = 'P-0PB25807RV732961JM4EURBY';
// const PAYPAL_YEARLY_PLAN_ID = 'P-7Y299590MU3194605M7DKEXY';

// PROD
const backendURL = 'https://us-central1-kikikata.cloudfunctions.net';
const PAYPAL_CLIENT_ID = 'AYEpP3nk_GOcuHPoQUSThmEACEbEpAhMmZE0yKUdQAAV7S2c5OE-akWJhAEpANuator03FV-PfAuW1Gg';
const PAYPAL_MONTHLY_PLAN_ID = 'P-778716672S961933RM4ED3YI';
const PAYPAL_YEARLY_PLAN_ID = 'P-35M331628R425561GM7CZXAQ';

function App() {
  const { isModalOpen } = useModal();

  return (
    <div className={isModalOpen ? 'modal-open-blur' : ''}>
      <OfflineOverlay />
      <Routes>
        {/* --- 2. PUBLIC ROUTES --- */}
        {/* These routes will redirect to /dashboard if a user is logged in. */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <MainPage />
            </PublicRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <PublicRoute>
              <PricingPage />
            </PublicRoute>
          }
        />
        <Route
          path="/user/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/user/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* --- 3. PRIVATE ROUTES --- */}
        {/* These routes will redirect to /user/login if a user is NOT logged in. */}
        <Route
          path="/user/settings"
          element={
            <PrivateRoute>
              <SettingsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/user/subscription"
          element={
            <PrivateRoute>
              <SubscriptionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/user/words"
          element={
            <PrivateRoute>
              <ProgressContentPage contentType="words" />
            </PrivateRoute>
          }
        />
        <Route
          path="/user/sentences"
          element={
            <PrivateRoute>
              <ProgressContentPage contentType="sentences" />
            </PrivateRoute>
          }
        />
        <Route
          path="/session/learn"
          element={
            <PrivateRoute>
              <LearnSessionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/session/review/sentences"
          element={
            <PrivateRoute>
              <ReviewSentencesSessionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/session/review/words"
          element={
            <PrivateRoute>
              <ReviewWordsSessionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/session/cram/sentences"
          element={
            <PrivateRoute>
              <CramSentencesSessionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/session/cram/words"
          element={
            <PrivateRoute>
              <CramWordsSessionPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/session/recent-unlocks/sentences"
          element={
            <PrivateRoute>
              <CramSentencesSessionPage mode="recent_unlocks" />
            </PrivateRoute>
          }
        />
        <Route
          path="/session/recent-unlocks/words"
          element={
            <PrivateRoute>
              <CramWordsSessionPage mode="recent_unlocks" />
            </PrivateRoute>
          }
        />
        <Route
          path="/session/end"
          element={
            <PrivateRoute>
              <SessionEndPage />
            </PrivateRoute>
          }
        />
        {/* --- 4. UNPROTECTED ROUTES (Accessible to everyone) --- */}
        <Route path="/user/action" element={<ActionPage />} />
        <Route path="/course/words" element={<CourseContentPage contentType="words" />} />
        <Route path="/course/sentences" element={<CourseContentPage contentType="sentences" />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

function AppWrapper() {
  const { userId } = useAuth();

  useEffect(() => {
    loadPayPalSdk(PAYPAL_CLIENT_ID);
  }, []);

  return (
    <Router>
      <ToastProvider>
        <ModalProvider>
          <App />
          {userId && <CrispChat userId={userId} />}
        </ModalProvider>
      </ToastProvider>
    </Router>
  );
}

export default AppWrapper;
export { backendURL, PAYPAL_MONTHLY_PLAN_ID, PAYPAL_YEARLY_PLAN_ID };
